<template>
  <teleport v-if="isClient" to="body" :disabled="!appendToBody">
    <transition
      :name="ns.b('fade')"
      @after-enter="afterEnter"
      @after-leave="afterLeave"
      @before-leave="beforeLeave"
    >
      <b-overlay
        v-show="visible"
        :mask="modal"
        :overlay-class="modalClass"
        :z-index="zIndex"
        @click="onModalClick"
      >
        <b-focus-trap
          loop
          :trapped="visible"
          :focus-trap-el="drawerRef"
          :focus-start-el="focusStartRef"
          @release-requested="onCloseRequested"
        >
          <div
            ref="drawerRef"
            aria-modal="true"
            :aria-label="title || undefined"
            :aria-labelledby="!title ? titleId : undefined"
            :aria-describedby="bodyId"
            :class="[ns.b(), direction, visible && 'open', containerClass]"
            :style="
              isHorizontal ? 'width: ' + drawerSize : 'height: ' + drawerSize
            "
            role="dialog"
            data-test-name="drawer"
            data-test-variant=""
            data-test-state=""
            @click.stop
          >
            <span ref="focusStartRef" :class="ns.e('sr-focus')" tabindex="-1" />
            <header v-if="withHeader" :class="ns.e('header')">
              <slot
                name="header"
                :close="handleClose"
                :title-id="titleId"
                :title-class="ns.e('title')"
              >
                <h4 :id="titleId" role="heading" :class="ns.e('title')">
                  {{ title }}
                </h4>
              </slot>

              <b-button
                v-if="showClose"
                small
                ghost
                :aria-label="t('b.drawer.close')"
                :icon="Close"
                @click="handleClose"
              />
            </header>
            <template v-if="rendered">
              <div :id="bodyId" :class="ns.e('body')">
                <slot />
              </div>
            </template>
            <div v-if="$slots.footer" :class="ns.e('footer')">
              <slot name="footer" />
            </div>
          </div>
        </b-focus-trap>
      </b-overlay>
    </transition>
  </teleport>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue'
import { useMounted } from '@vueuse/core'
import { Close } from '@bigin/icons-vue'

import { BOverlay } from '@bigin/components/overlay'
import BFocusTrap from '@bigin/components/focus-trap'
import { useDialog } from '@bigin/components/dialog'
import BButton from '@bigin/components/button'
import { addUnit } from '@bigin/utils'
import { useLocale, useNamespace } from '@bigin/hooks'
import { drawerEmits, drawerProps } from './drawer'

export default defineComponent({
  name: 'BDrawer',
  components: {
    BOverlay,
    BFocusTrap,
    BButton,
  },
  props: drawerProps,
  emits: drawerEmits,

  setup(props) {
    const drawerRef = ref<HTMLElement>()
    const focusStartRef = ref<HTMLElement>()
    const ns = useNamespace('drawer')
    const { t } = useLocale()
    const isClient = useMounted()

    const isHorizontal = computed(
      () => props.direction === 'rtl' || props.direction === 'ltr'
    )
    const drawerSize = computed(() =>
      addUnit(isHorizontal.value ? props.width : props.height)
    )

    return {
      ...useDialog(props, drawerRef),
      drawerRef,
      focusStartRef,
      isHorizontal,
      drawerSize,
      ns,
      t,
      Close,
      isClient,
    }
  },
})
</script>
